/**
 * Task Status Indicator Utility
 *
 * This utility provides functions and definitions for task status indicators
 * based on completion status, conversation existence, and validation state.
 */

// Status types for tasks
export const TASK_STATUS = {
  NEW: 'new',                                // Task is added but no conversation exists
  IN_PROGRESS: 'in_progress',                // Conversation exists but task is not completed
  AI_VERIFIED: 'ai_verified',                // AI has verified the task
  COMPLETED: 'completed',                    // Human has marked task as done
  VALIDATED_INTERNAL: 'validated_internal',   // Approved by internal validator (blue)
  VALIDATED_EXTERNAL: 'validated_external',   // Approved by external validator (green)
  VALIDATED_BOTH: 'validated_both',           // Approved by both internal and external (split blue+green)
  VALIDATION_STALE: 'validation_stale',       // Content changed after validation (amber)
  CHANGES_REQUESTED: 'changes_requested'      // Validator requested changes (red)
};

/**
 * Get the appropriate status for a task
 * @param {Object} task - The task object
 * @param {Array} completedTasks - Array of completed task IDs
 * @param {Array} conversations - Array of conversations
 * @param {Object} validationStatus - Validation status from validation summary
 * @returns {string} The task status
 */
export const getTaskStatus = (task, completedTasks = [], conversations = [], validationStatus = null) => {
  if (!task || !task.id) {
    return TASK_STATUS.NEW;
  }

  // Check validation states first (highest priority when validators are assigned)
  if (validationStatus) {
    const intVal = validationStatus.internal_validation;
    const extVal = validationStatus.external_validation;
    const hasValidators = validationStatus.validators &&
      (validationStatus.validators.internal?.length > 0 || validationStatus.validators.external?.length > 0);

    if (hasValidators) {
      // Check for stale validations
      const anyStale = (intVal && intVal.is_stale) || (extVal && extVal.is_stale);
      if (anyStale) return TASK_STATUS.VALIDATION_STALE;

      // Check for changes requested
      const hasChangesRequested = (intVal && intVal.status === 'changes_requested') ||
        (extVal && extVal.status === 'changes_requested');
      if (hasChangesRequested) return TASK_STATUS.CHANGES_REQUESTED;

      // Check for approvals
      const hasInternal = intVal && intVal.status === 'approved';
      const hasExternal = extVal && extVal.status === 'approved';
      if (hasInternal && hasExternal) return TASK_STATUS.VALIDATED_BOTH;
      if (hasInternal) return TASK_STATUS.VALIDATED_INTERNAL;
      if (hasExternal) return TASK_STATUS.VALIDATED_EXTERNAL;
    }
  }

  // Check if task is marked as completed
  if (completedTasks.includes(task.id)) {
    return TASK_STATUS.COMPLETED;
  }

  // Check if there is a conversation with messages for this task
  const conversationWithMessages = conversations.find(
    conv => {
      const isTaskConversation = conv.task_id === task.id || conv.taskId === task.id;
      if (!isTaskConversation) return false;
      if (conv.message_count !== undefined) {
        return conv.message_count > 0;
      }
      if (conv.messages !== undefined) {
        return conv.messages.length > 0;
      }
      return false;
    }
  );

  if (conversationWithMessages) {
    return TASK_STATUS.IN_PROGRESS;
  }

  return TASK_STATUS.NEW;
};

/**
 * Get the SVG component for a specific task status
 * @param {string} status - The task status
 * @returns {Object} SVG properties for the indicator
 */
export const getStatusIndicator = (status) => {
  switch (status) {
    case TASK_STATUS.COMPLETED:
      return {
        icon: (
          <svg id="Tick" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 28.027 28.027">
            <path id="Path_58116" data-name="Path 58116" d="M28.027,14.014l-.943,1.722.469,1.906L26.2,19.059l-.044,1.963-1.68,1.017-.55,1.885-1.884.55-1.017,1.679-1.963.044-1.418,1.358-1.906-.469-1.722.943-1.721-.943-1.906.469L8.968,26.2l-1.963-.044-1.017-1.68L4.1,23.923l-.55-1.884-1.68-1.017-.044-1.963L.474,17.641l.469-1.906L0,14.014l.943-1.721L.474,10.386,1.832,8.968l.044-1.963,1.68-1.017L4.1,4.1l1.885-.55,1.016-1.68,1.963-.044L10.386.474l1.906.469L14.014,0l1.722.943L17.641.474l1.418,1.358,1.963.044,1.017,1.68,1.885.55.55,1.885,1.679,1.016L26.2,8.968l1.358,1.418-.469,1.906Z" fill="#62c148"></path>
            <path id="Path_58117" data-name="Path 58117" d="M289.508,319.711l-3.968-3.968,1.318-1.318,2.52,2.52,7.586-9.166,1.436,1.189Z" transform="translate(-277.955 -299.731)" fill="#fff"></path>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    case TASK_STATUS.AI_VERIFIED:
      return {
        icon: (
          <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="14" fill="#FF9800"/>
            <path d="M11.5,16.2l-3.5-3.5l1.1-1.1l2.4,2.4l5.8-5.8l1.1,1.1L11.5,16.2z" fill="#FFF"/>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    case TASK_STATUS.IN_PROGRESS:
      return {
        icon: (
          <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="14" fill="#FF9800"/>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    case TASK_STATUS.NEW:
      return {
        icon: (
          <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="13" fill="transparent" stroke="#ffcccc" strokeWidth="2"/>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    case TASK_STATUS.VALIDATED_INTERNAL:
      // Blue circle with checkmark
      return {
        icon: (
          <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="14" fill="#1976d2"/>
            <path d="M11.5,16.2l-3.5-3.5l1.1-1.1l2.4,2.4l5.8-5.8l1.1,1.1L11.5,16.2z" fill="#FFF"/>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    case TASK_STATUS.VALIDATED_EXTERNAL:
      // Green circle with checkmark
      return {
        icon: (
          <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="14" fill="#2e7d32"/>
            <path d="M11.5,16.2l-3.5-3.5l1.1-1.1l2.4,2.4l5.8-5.8l1.1,1.1L11.5,16.2z" fill="#FFF"/>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    case TASK_STATUS.VALIDATED_BOTH:
      // Split blue+green circle with checkmark
      return {
        icon: (
          <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <clipPath id="leftHalf"><rect x="0" y="0" width="14" height="28"/></clipPath>
              <clipPath id="rightHalf"><rect x="14" y="0" width="14" height="28"/></clipPath>
            </defs>
            <circle cx="14" cy="14" r="14" fill="#1976d2" clipPath="url(#leftHalf)"/>
            <circle cx="14" cy="14" r="14" fill="#2e7d32" clipPath="url(#rightHalf)"/>
            <path d="M11.5,16.2l-3.5-3.5l1.1-1.1l2.4,2.4l5.8-5.8l1.1,1.1L11.5,16.2z" fill="#FFF"/>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    case TASK_STATUS.VALIDATION_STALE:
      // Amber/yellow circle with refresh icon
      return {
        icon: (
          <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="14" fill="#f9a825"/>
            <path d="M18.5,14c0,2.5-2,4.5-4.5,4.5S9.5,16.5,9.5,14h1.5c0,1.7,1.3,3,3,3s3-1.3,3-3s-1.3-3-3-3v2l-3-3l3-3v2c2.5,0,4.5,2,4.5,4.5z" fill="#FFF"/>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    case TASK_STATUS.CHANGES_REQUESTED:
      // Red circle with exclamation
      return {
        icon: (
          <svg width="24" height="24" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="14" fill="#d32f2f"/>
            <rect x="12.5" y="8" width="3" height="8" rx="1.5" fill="#FFF"/>
            <circle cx="14" cy="19.5" r="1.5" fill="#FFF"/>
          </svg>
        ),
        position: { top: 10, right: 10 },
      };

    default:
      return null;
  }
};
